import { loader } from '@play-co/timestep-core';
import {
  animate,
  LinearAddFilter,
  View,
  ViewOpts,
} from '@play-co/timestep-core/ui';
import { createEmitter } from 'src/lib/Emitter';
import LazyImageView from 'src/lib/ui/components/LazyImageView';
import uiConfig from 'src/lib/ui/config';
import { animDuration, getScreenDimensions } from 'src/lib/utils';
import * as Decoration from 'src/replicant/decoration';
import type {
  DecorationItem,
  DecorationRoomID,
  DecorationSlot,
  DecorationTier,
} from 'src/replicant/decoration/decoration.ruleset';
import { assertNever } from 'src/replicant/utils';
import StateObserver, { user } from 'src/StateObserver';
import {
  getDecorationPath,
  getSelectedItemForSlot,
  getSelectedItems,
  isFloorSlot,
  isWallSlot,
  shouldSkipShowupAnimation,
} from './helpers';

export type DecorationSlotOpts = {
  width: number;
  height: number;
  x: number;
  y: number;
  zIndex: number;
  scale?: number;
};

export type DecorationEditOpts = {
  x: number;
  y: number;
};

export type DecorationMode = 'progression' | 'classic';

/**
 * Decoration view abstraction
 * Used to organize access to displayed slots information and change image api
 * Listen for slot change for room
 */
export default class DecorationView extends View {
  // Current room decoration
  private roomID: DecorationRoomID;

  // Available slots for this view
  private slotsIDs: Set<DecorationSlot> = new Set();

  // Default images
  private default: Record<number, string> = {};

  // Default edit buttons config
  private buttons: Record<number, DecorationEditOpts> = {};

  // Images hash map
  protected images: Record<number, LazyImageView> = {};

  // Slots configs
  private configs: Record<number, DecorationSlotOpts> = {};

  // Remember updated items
  private updated: Record<number, DecorationItem> = {};

  /**
   * Subscribe on resize event
   */
  constructor(room: DecorationRoomID, viewOpts: ViewOpts) {
    super(viewOpts);

    // Set room id
    this.roomID = room;

    // Listen resize event
    createEmitter(this, ({ ui }) => ui.screenSize).addListener(() => {
      this.resize();
    });
  }

  /**
   * Get slots configuration
   */
  public getSlotsConfig(slot: DecorationSlot) {
    return this.configs[slot];
  }

  /**
   * Get edit buttons configuration
   */
  public getButtonsConfig(slot: DecorationSlot) {
    return this.buttons[slot];
  }

  /**
   * Get edit buttons configuration
   */
  public getDefaultImage(
    slot: DecorationSlot,
    mode: DecorationMode,
  ): string | undefined {
    const { user } = StateObserver.getState();

    switch (this.roomID) {
      case 'living':
        return this.default[slot];
      case 'toilet':
        return this.default[slot];
      default:
        assertNever(this.roomID);
    }
  }

  /**
   * Get available slots for edit
   */
  public getSlotsIDs() {
    return this.slotsIDs;
  }

  /**
   * Gte room ID
   */
  public getRoom() {
    return this.roomID;
  }

  /**
   * Update image by image id
   */
  public async updateImageViewWithAnimation(
    slot: DecorationSlot,
    item: DecorationItem,
  ) {
    // Do not update same item to same item
    if (this.updated[slot] === item) return;
    this.updated[slot] = item;

    const imagePath = getDecorationPath(item.image);

    await this.updateImageAnimation(slot, imagePath);
  }

  /**
   * Update view by selected items, use it when you want to set some view decoration for view
   */
  public updateSelectedItems(
    selectedMap: Map<DecorationSlot, DecorationItem | DecorationTier | null>,
    mode: DecorationMode,
  ) {
    // Go throw all slots
    this.slotsIDs.forEach((id) => {
      // Get selected item
      const selected = selectedMap.get(id);

      // Default image url for this slot
      const url = getDecorationPath(this.getDefaultImage(id, mode));
      if (!selected) return this.updateImageView(id, url);

      // Get image url of selected image
      const path = getDecorationPath(selected.image);

      // Set selected image
      return this.updateImageView(id, path);
    });
  }

  /**
   * Clear all previous decoration items
   */
  public clear() {
    // Go throw all slots
    this.slotsIDs.forEach((id) => {
      // Set selected image
      return this.updateImageView(id, undefined);
    });
  }

  /**
   * Animate slot
   */
  private async updateImageAnimation(
    slot: DecorationSlot,
    imagePath: string | undefined,
  ) {
    const image = this.images[slot];

    // Update without animation
    if (shouldSkipShowupAnimation(this.roomID, slot)) {
      image.setImage(imagePath);
      return;
    }

    // Hide only
    if (!imagePath) {
      return animate(image)
        .commit()
        .then({ scale: 0 }, animDuration, animate.easeInCubic)
        .then(() => {
          image.hide();
        })
        .toPromise();
    }

    // Default start animation
    await animate(image)
      .commit()
      .then({ scale: 0 }, animDuration, animate.easeInCubic)
      .toPromise();

    // Load image
    await image.updateImage(imagePath);

    // Default finish animation
    return animate(image)
      .commit()
      .then(() => {
        image.show();
      })
      .then({ scale: 1 }, animDuration * 3, animate.easeOutElastic)
      .toPromise();
  }

  /**
   * Subscribe listeners for states
   */
  protected listenSlotChangeEvent() {
    // Listen permanent items change event
    createEmitter(this, ({ user }) =>
      Decoration.getters.getSelectedDecorationsState(user),
    ).addListener((selected) =>
      // prettier-ignore
      this.updateSelectedItems(getSelectedItems(this.roomID, selected), 'classic'),
    );
  }

  /**
   * Create slot abstraction
   */
  protected createSlot(
    slot: DecorationSlot,
    defaultImage: string,
    config: DecorationSlotOpts,
    button: DecorationEditOpts,
  ) {
    // Set default images to abstraction layer
    this.setDefaultSlotImages(slot, defaultImage);

    // Set edit buttons buttons to abstraction layer
    this.setEditSlotButtons(slot, button);

    // Selected (saved in state) item images
    const selected = getSelectedItemForSlot(this.roomID, slot);

    // Some images we can skip
    const room = this.getRoom();
    const cantBeEmpty = isWallSlot(room, slot) || isFloorSlot(room, slot);

    // Do not show some default images
    if (!cantBeEmpty) {
      void this.addImageView(slot, undefined, config);
      return;
    }

    // If we have selected item set image
    // Warning: Image maybe null for empty slots
    if (selected) {
      void this.addImageView(slot, getDecorationPath(selected.image), config);
    } else {
      // Nothing selected just show default one
      void this.addImageView(slot, getDecorationPath(defaultImage), config);
    }
  }

  /**
   * Update image by image id
   */
  protected updateImageView(id: DecorationSlot, url: string | undefined) {
    const image = this.images[id];

    if (image) {
      image.setImage(url);
      image.show();
    }
  }

  /**
   * Add image to view
   */
  private async addImageView(
    slot: DecorationSlot,
    image: string | undefined,
    opts: DecorationSlotOpts,
  ) {
    const scale = opts.scale ?? 1;

    const view = new LazyImageView({
      tag: `slot_${slot}`,
      superview: this,
      centerAnchor: true,
      ...opts,
      noSpinner: true,
      canHandleEvents: false,

      // We can't use scale directly here, or the item can get misplaced.
      // So better scale width & height instead.
      scale: 1,
      width: opts.width * scale,
      height: opts.height * scale,
    });

    this.images[slot] = view;
    this.configs[slot] = opts;
    this.slotsIDs.add(slot);

    // Hide empty slots
    if (image) {
      // If image already loaded skip animation
      if (loader.cache[image]) {
        this.images[slot].setImage(image);
      } else {
        await this.updateImageAnimation(slot, image);
      }
    } else {
      view.hide();
    }
  }

  /**
   * Remember default slot images
   */
  private setDefaultSlotImages(slot: DecorationSlot, image: string) {
    this.default[slot] = image;
  }

  /**
   * Set edit buttons config
   */
  private setEditSlotButtons(slot: DecorationSlot, edit: DecorationEditOpts) {
    this.buttons[slot] = edit;
  }

  /**
   * Resize view
   */
  protected resize() {
    const current = { width: this.style.width, height: this.style.height };
    const target = {
      width: uiConfig.width,
      height: getScreenDimensions().height,
    };

    const scale = target.height / current.height;
    const sx = (current.width * scale - current.width) * 0.5;

    super.updateOpts({
      scale: scale,
      offsetX: -sx,
    });
  }

  public async animateFilter(id: DecorationSlot, opacity: number) {
    const image = this.images[id];
    const filter = image.getFilter() as LinearAddFilter;

    if (image.style.visible) {
      await animate(filter.color)
        .then({ a: opacity }, animDuration, animate.easeInCubic)
        .toPromise();
    } else {
      filter.color.a = opacity;
    }
  }

  public attachFilters() {
    for (let image of Object.values(this.images)) {
      image.setFilter(new LinearAddFilter('#FFFFFF00'));
    }
  }
}
